import React, { useState, useRef } from 'react';
import './CSSEditor.css';

const CSSEditor = ({ value, onChange, placeholder = "Enter CSS rules..." }) => {
  const [activeTab, setActiveTab] = useState('visual'); // 'visual' or 'code'
  const textareaRef = useRef(null);

  // Insert CSS snippet
  const insertCSS = (cssSnippet) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    const newText = value.substring(0, start) + cssSnippet + value.substring(end);
    onChange(newText);
    
    // Set cursor position after the snippet
    setTimeout(() => {
      const newPosition = start + cssSnippet.length;
      textarea.setSelectionRange(newPosition, newPosition);
      textarea.focus();
    }, 10);
  };

  // CSS snippets for common styling
  const cssSnippets = [
    {
      label: 'Section Style',
      css: `
.ddr-section {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 20px;
}`,
      description: 'Basic section styling'
    },
    {
      label: 'Header Style',
      css: `
.section-header {
  background: #f8fafc;
  color: #1e293b;
  font-weight: 600;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
}`,
      description: 'Section header styling'
    },
    {
      label: 'Table Style',
      css: `
.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  text-align: left;
}

.data-table th {
  background: #f9fafb;
  font-weight: 600;
}`,
      description: 'Table styling'
    },
    {
      label: 'Form Group',
      css: `
.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.form-group label {
  font-weight: 500;
  margin-bottom: 4px;
  color: #374151;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
}`,
      description: 'Form styling'
    },
    {
      label: 'Grid Layout',
      css: `
.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}`,
      description: 'Responsive grid layout'
    },
    {
      label: 'Flexbox Layout',
      css: `
.flex-container {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
}`,
      description: 'Flexbox layout'
    }
  ];

  // Common CSS properties for quick access
  const cssProperties = [
    { label: 'Color', value: 'color: #000000;' },
    { label: 'Background', value: 'background: #ffffff;' },
    { label: 'Border', value: 'border: 1px solid #e5e7eb;' },
    { label: 'Padding', value: 'padding: 16px;' },
    { label: 'Margin', value: 'margin: 16px;' },
    { label: 'Font Size', value: 'font-size: 14px;' },
    { label: 'Font Weight', value: 'font-weight: 600;' },
    { label: 'Border Radius', value: 'border-radius: 8px;' },
    { label: 'Text Align', value: 'text-align: center;' },
    { label: 'Display Flex', value: 'display: flex;' },
    { label: 'Display Grid', value: 'display: grid;' },
    { label: 'Width', value: 'width: 100%;' }
  ];

  // DDR-specific selectors
  const ddrSelectors = [
    '.ddr-section',
    '.section-header', 
    '.section-content',
    '.data-table',
    '.form-group',
    '.label-value-pair',
    '.field-label',
    '.field-value',
    '.report-header',
    '.report-title',
    '.group-container',
    '.group-header'
  ];

  return (
    <div className="css-editor">
      {/* Tab Headers */}
      <div className="editor-tabs">
        <button
          className={`tab-button ${activeTab === 'visual' ? 'active' : ''}`}
          onClick={() => setActiveTab('visual')}
        >
          🎨 Visual
        </button>
        <button
          className={`tab-button ${activeTab === 'code' ? 'active' : ''}`}
          onClick={() => setActiveTab('code')}
        >
          💻 Code
        </button>
      </div>

      {activeTab === 'visual' && (
        <div className="visual-editor">
          {/* Toolbar */}
          <div className="editor-toolbar">
            <div className="toolbar-section">
              <h4 className="toolbar-title">CSS Snippets</h4>
              <div className="toolbar-group">
                {cssSnippets.map((snippet, index) => (
                  <button
                    key={index}
                    className="toolbar-btn snippet-btn"
                    onClick={() => insertCSS(snippet.css)}
                    title={snippet.description}
                  >
                    {snippet.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="toolbar-section">
              <h4 className="toolbar-title">Quick Properties</h4>
              <div className="toolbar-group">
                {cssProperties.map((prop, index) => (
                  <button
                    key={index}
                    className="toolbar-btn property-btn"
                    onClick={() => insertCSS(prop.value)}
                    title={`Insert ${prop.label}`}
                  >
                    {prop.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="toolbar-section">
              <h4 className="toolbar-title">DDR Selectors</h4>
              <div className="toolbar-group">
                {ddrSelectors.map((selector, index) => (
                  <button
                    key={index}
                    className="toolbar-btn selector-btn"
                    onClick={() => insertCSS(`${selector} {\n  \n}`)}
                    title={`Insert ${selector} rule`}
                  >
                    {selector}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="visual-textarea css-textarea"
            rows={15}
          />
        </div>
      )}

      {activeTab === 'code' && (
        <div className="code-editor-content">
          <div className="code-editor-header">
            <div className="editor-info">
              <span className="editor-language">CSS</span>
              <span className="editor-lines">{value.split('\n').length} lines</span>
            </div>
          </div>
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="code-textarea css-textarea"
            rows={20}
            spellCheck={false}
          />
        </div>
      )}

      {/* Help Section */}
      <div className="css-editor-help">
        <details>
          <summary>💡 CSS Editor Help</summary>
          <div className="help-content">
            <h5>Tips for Custom CSS:</h5>
            <ul>
              <li>Use the <strong>Visual</strong> tab for guided editing with snippets and quick properties</li>
              <li>Use the <strong>Code</strong> tab for direct CSS editing with syntax highlighting</li>
              <li>Target DDR-specific elements using the provided selectors</li>
              <li>Use <code>!important</code> to override default styles when necessary</li>
              <li>Test your changes by saving and viewing the DDR preview</li>
            </ul>
            
            <h5>Common DDR Classes:</h5>
            <ul>
              <li><code>.ddr-section</code> - Individual report sections</li>
              <li><code>.section-header</code> - Section title headers</li>
              <li><code>.data-table</code> - Data tables within sections</li>
              <li><code>.label-value-pair</code> - Label-value form layouts</li>
              <li><code>.group-container</code> - Field grouping containers</li>
            </ul>
          </div>
        </details>
      </div>
    </div>
  );
};

export default CSSEditor; 